import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Tooltip,
  Autocomplete,
  Checkbox,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import { CacheKey } from '../../../types/cache-keys';
import ResourceRelationPanel from '../Panels/ResourceRelationPanel';
import TagManagementSection from '../Common/TagManagementSection';
import SnackableCourseButton from '../SnackableCourse/SnackableCourseButton';
import SnackableCourseManager from '../SnackableCourse/SnackableCourseManager';
import { asArray } from '../../../support/utils';
import { useAttachedItemsUpdateMutation } from '../../../hooks/gtm-solution-hooks';
import { ProductFeature, ProductUseCase } from '../../../types/product';
import {
  ProductFeatureVersionLink,
  useAddProductUseCaseMutation,
  useFetchProductFeatures,
  useFetchProductVersionFeatures,
  useFetchSingleProduct,
  useFetchSingleProductUseCase,
  useUpdateProductUseCaseMutation
} from '../../../hooks/product-hooks';
import AdminConfirmDialog from '../Common/AdminConfirmDialog';
import { VersionScope } from "../../../types/snackableCourse";
import {
  buildRecommendedVersionScope,
  hasMeaningfulVersionScope,
} from "../../../support/versionScopeUtils";

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  productId: number;
  useCaseId?: number;
  onClose: () => void;
  onSaved: () => void;
  readOnly?: boolean;
  versionScope?: VersionScope | null;
};

const toId = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof value === "object" && value !== null && "id" in value) {
    return toId((value as { id?: unknown }).id);
  }

  return null;
};

const getJoinFeatureId = (join: ProductFeatureVersionLink): number | null => {
  return toId((join as any)?.product_feature) || toId((join as any)?.feature);
};

export default function ProductUseCaseEditorDialog({
                                                     open,
                                                     mode,
                                                     productId,
                                                     useCaseId,
                                                     onClose,
                                                     onSaved,
                                                     readOnly = false,
                                                     versionScope = null,
                                                   }: Props) {
  const isEdit = mode === "edit" && !!useCaseId;

  const { data: product, isLoading: productLoading } = useFetchSingleProduct(productId);
  const { data: useCaseData, isLoading: useCaseLoading } = useFetchSingleProductUseCase(productId, useCaseId);

  const effectiveVersionScope: VersionScope | null = useMemo(() => {
    return buildRecommendedVersionScope({
      product,
      productId,
      versionScope,
    });
  }, [product, productId, versionScope]);

  const versionScoped = hasMeaningfulVersionScope(effectiveVersionScope);

  const useCase: ProductUseCase = useMemo(() => {
    if (isEdit) {
      if (productLoading && useCaseLoading) return null;
      if (useCaseData) return useCaseData;
      const found = asArray(product?.use_case).find(s => s.id === useCaseId);
      return found ?? null;
    }
    return null;
  }, [isEdit, useCaseId, product, productLoading, useCaseData, useCaseLoading]);

  const { mutateAsync: addUseCase, isPending: addingUseCase } = useAddProductUseCaseMutation(productId);
  const { mutateAsync: updateUseCase, isPending: updatingUseCase } = useUpdateProductUseCaseMutation(productId);
  const { mutateAsync: updateJoins, isPending: updatingJoins } =
    useAttachedItemsUpdateMutation<ProductFeature, ProductUseCase>({
      type: CacheKey.ProductUseCaseFeature,
      productId
    });

  const [form, setForm] = useState<any>({
    name: '',
    short_description: "",
    description: '',
    icon: '',
    product: productId,
  });
  const [initialForm, setInitialForm] = useState<any>({
    name: '',
    short_description: "",
    description: '',
    icon: '',
    product: productId,
  });

  const { data: features, isLoading: featuresLoading } = useFetchProductFeatures(productId);

  const {
    data: versionFeatureLinks = [],
    isLoading: versionFeaturesLoading,
  } = useFetchProductVersionFeatures(
    effectiveVersionScope?.product_version_id,
    { detailed: true, size: 2000 },
    {
      enabled: open && versionScoped && !!effectiveVersionScope?.product_version_id,
    }
  );

  const versionFeatureIds = useMemo(() => {
    if (!versionScoped || !effectiveVersionScope?.product_version_id) return null;

    return new Set(
      (versionFeatureLinks ?? [])
        .map((join) => getJoinFeatureId(join))
        .filter((id): id is number => id != null)
    );
  }, [versionScoped, effectiveVersionScope?.product_version_id, versionFeatureLinks]);

  const featuresById = useMemo(() => {
    const map = new Map<ProductFeature['id'], ProductFeature>();
    for (const feature of features ?? []) map.set(feature.id, feature);
    return map;
  }, [features]);

  const featureOptions = useMemo(() => {
    if (!features) return [];

    if (!versionScoped || !versionFeatureIds) {
      return features;
    }

    return features.filter((feature) => versionFeatureIds.has(Number(feature.id)));
  }, [features, versionScoped, versionFeatureIds]);

  const [selectedFeatures, setSelectedFeatures] = useState<ProductFeature[]>([]);
  const [initialSelectedFeatureIds, setInitialSelectedFeatureIds] = useState<ProductFeature['id'][]>([]);
  const [courseManagerOpen, setCourseManagerOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const visibleSelectedFeatures = useMemo(() => {
    if (!versionScoped || !versionFeatureIds) return selectedFeatures;
    return selectedFeatures.filter((feature) => versionFeatureIds.has(Number(feature.id)));
  }, [selectedFeatures, versionScoped, versionFeatureIds]);

  useEffect(() => {
    if (!open) return;

    if (!isEdit || !useCase) {
      const emptyForm = {
        name: '',
        short_description: "",
        description: '',
        icon: '',
        product: productId,
      };
      setForm(emptyForm);
      setInitialForm(emptyForm);
      setSelectedFeatures([]);
      setInitialSelectedFeatureIds([]);
      return;
    }

    const nextForm = {
      name: useCase.name ?? '',
      short_description: useCase.short_description ?? "",
      description: useCase.description ?? '',
      icon: useCase.icon ?? '',
      product: productId,
    };

    const featureIds = [
      ...new Set(asArray(useCase?.product_feature ?? []).map((f: ProductFeature) => f.id))
    ];
    const nextSelectedFeatures = featureIds.map((id) => featuresById.get(id)).filter(Boolean);

    setForm(nextForm);
    setInitialForm(nextForm);
    setSelectedFeatures(nextSelectedFeatures);
    setInitialSelectedFeatureIds(nextSelectedFeatures.map((f: ProductFeature) => f.id));
  }, [open, isEdit, useCase, productId, featuresById]);

  const canManageRelations = isEdit;

  const saving =
    addingUseCase ||
    updatingUseCase ||
    updatingJoins ||
    featuresLoading ||
    useCaseLoading;

  const loading = saving || versionFeaturesLoading;

  const hasUnsavedChanges = useMemo(() => {
    const currentIds = selectedFeatures.map((f) => f.id).sort((a, b) => a - b);
    const initialIds = [...initialSelectedFeatureIds].sort((a, b) => a - b);

    return (
      form.name !== initialForm.name ||
      form.short_description !== initialForm.short_description ||
      form.description !== initialForm.description ||
      form.icon !== initialForm.icon ||
      JSON.stringify(currentIds) !== JSON.stringify(initialIds)
    );
  }, [selectedFeatures, initialSelectedFeatureIds, form.name, initialForm.name, form.short_description, initialForm.short_description, form.description, initialForm.description, form.icon, initialForm.icon]);

  const resetAndClose = () => {
    setForm({
      name: '',
      short_description: "",
      description: '',
      icon: '',
      product: productId,
    });
    setInitialForm({
      name: '',
      short_description: "",
      description: '',
      icon: '',
      product: productId,
    });
    setSelectedFeatures([]);
    setInitialSelectedFeatureIds([]);
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleCloseDialog = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    resetAndClose();
  };

  const handleSave = async () => {
    if (readOnly) {
      handleCloseDialog();
      return;
    }

    let finalId = null;
    if (mode === 'create') {
      const created: any = await addUseCase({ ...form, product: productId });
      finalId = created?.id;
    } else if (isEdit && useCaseId) {
      await updateUseCase({ ...form, id: useCaseId, product: productId });
      finalId = useCaseId;
    }

    if (finalId) {
      const selectedFeatureIds = new Set((selectedFeatures ?? []).map(f => f.id));
      await updateJoins({ selectedItems: selectedFeatureIds, attachingToId: finalId });
    }

    onSaved();
    resetAndClose();
  };

  const handleFeatureChange = (newValues: ProductFeature[]) => {
    if (readOnly) return;

    if (!versionScoped || !versionFeatureIds) {
      setSelectedFeatures(newValues);
      return;
    }

    const scopedOptionIds = new Set(featureOptions.map((feature) => Number(feature.id)));
    const preservedOutOfScope = selectedFeatures.filter(
      (feature) => !scopedOptionIds.has(Number(feature.id))
    );

    const nextById = new Map<number, ProductFeature>();
    preservedOutOfScope.forEach((feature) => nextById.set(Number(feature.id), feature));
    newValues.forEach((feature) => nextById.set(Number(feature.id), feature));

    setSelectedFeatures(Array.from(nextById.values()));
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {readOnly
                ? (mode === "create"
                  ? "View Product Use Case"
                  : `View Product Use Case: ${form?.name || `#${useCaseId}`}`)
                : (mode === "create"
                  ? "Create Product Use Case"
                  : `Product Use Case: ${form?.name || `#${useCaseId}`}`)}
            </Typography>

            {isEdit && (
              <SnackableCourseButton
                entityType="product_use_case"
                entityId={useCaseId}
                entityName={form.name}
                disabled={!isEdit}
                versionScope={effectiveVersionScope}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading && (
            <Box display='flex' alignItems='center' gap={2} py={1}>
              <CircularProgress size={18} />
              <Typography variant='body2'>Loading…</Typography>
            </Box>
          )}

          {versionScoped && (
            <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Version-scoped editing
              </Typography>
              <Typography variant="body2">
                Supporting feature options are filtered to the selected/recommended version. Existing out-of-scope feature mappings are preserved unless edited from View All Versions.
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                {effectiveVersionScope?.doc_version && <Chip size="small" label={`Version: ${effectiveVersionScope.doc_version}`} />}
                {effectiveVersionScope?.deployment_scope && <Chip size="small" label={`Deployment: ${effectiveVersionScope.deployment_scope}`} />}
                {effectiveVersionScope?.version_source && <Chip size="small" label={`Source: ${effectiveVersionScope.version_source}`} />}
              </Box>
            </Alert>
          )}

          <Box mt={1}>
            <Typography variant='subtitle2'>Product</Typography>
            <Typography variant='body2' color='text.secondary'>
              {product?.name ?? `Product #${productId}`}
            </Typography>
          </Box>

          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={form.name ?? ""}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, name: e.target.value }))}
            disabled={readOnly}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Short Description"
            value={form.short_description ?? ""}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, short_description: e.target.value }))}
            multiline
            minRows={2}
            disabled={readOnly}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={form.description ?? ""}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, description: e.target.value }))}
            multiline
            minRows={2}
            disabled={readOnly}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Icon"
            value={form.icon ?? ""}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, icon: e.target.value }))}
            helperText="Optional: URL or icon identifier"
            disabled={readOnly}
          />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Supporting Features ({visibleSelectedFeatures.length})
          </Typography>

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={featureOptions ?? []}
            value={visibleSelectedFeatures}
            disabled={readOnly}
            isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id}
            getOptionLabel={(opt: any) => opt?.name ?? ""}
            onChange={(_, newValues) => handleFeatureChange(newValues)}
            renderOption={(props, option: any, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                <Tooltip title={option.description || ""} placement="right" arrow>
                  <Box sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{option.name}</Box>
                </Tooltip>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={featuresLoading || versionFeaturesLoading ? "Loading features..." : "Select Features"}
                placeholder={featuresLoading || versionFeaturesLoading ? "Loading features..." : "Search features..."}
              />
            )}
          />

          <Divider sx={{ my: 3 }} />

          {canManageRelations && (
            <Box mb={3}>
              <ResourceRelationPanel
                joinEndpoint="db/product-use-case-to-resource"
                joinCacheKey={CacheKey.ProductUseCaseToResource}
                parentField="product_use_case"
                parentId={useCaseId ?? null}
                title="Resources"
                showNameColumn={false}
                maxHeight={300}
                invalidateKeys={[CacheKey.ProductUseCase]}
                disabled={!canManageRelations}
                readOnly={readOnly}
                versionScope={effectiveVersionScope}
                showVersionScopeColumn={true}
              />
            </Box>
          )}

          <TagManagementSection
            parentId={useCaseId ?? null}
            parentType={CacheKey.ProductUseCase}
            disabled={!canManageRelations || readOnly}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              Save
            </Button>
          )}
        </DialogActions>

        {courseManagerOpen && (
          <SnackableCourseManager
            open={courseManagerOpen}
            entityType="product_use_case"
            entityId={useCaseId}
            entityName={form.name}
            versionScope={effectiveVersionScope}
            onClose={() => setCourseManagerOpen(false)}
          />
        )}
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You have unsaved changes in this product use case editor.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}